import { collection, doc, getDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';

// TypeScript interfaces
export interface MonthlyCampaignResult {
  uniqueCode: string;
  campaignType: string;
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  orders: number;
  units: number;
}

export interface MonthlyCampaignData {
  asin: string;
  campaigns: MonthlyCampaignResult[];
  lastUpdated: Date;
}

/**
 * Get Monthly Campaign Results data for a specific ASIN
 * @param companyName - The company name
 * @param asin - The ASIN to get data for
 * @returns Promise<MonthlyCampaignData | null>
 */
export async function getMonthlyCampaignResultsData(
  companyName: string, 
  asin: string
): Promise<MonthlyCampaignData | null> {
  try {
    const docRef = doc(db, `pmt2_campaignresults_month/${companyName}/asins/${asin}`);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        asin: data.asin,
        campaigns: data.campaigns || [],
        lastUpdated: data.lastUpdated?.toDate() || new Date()
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching Monthly Campaign Results data:', error);
    throw error;
  }
}

/**
 * Get all Monthly Campaign Results data for a company
 * @param companyName - The company name
 * @returns Promise<MonthlyCampaignData[]>
 */
export async function getAllMonthlyCampaignResultsData(
  companyName: string
): Promise<MonthlyCampaignData[]> {
  try {
    const collectionRef = collection(db, `pmt2_campaignresults_month/${companyName}/asins`);
    const q = query(collectionRef, orderBy('asin'));
    const querySnapshot = await getDocs(q);
    
    const results: MonthlyCampaignData[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      results.push({
        asin: data.asin,
        campaigns: data.campaigns || [],
        lastUpdated: data.lastUpdated?.toDate() || new Date()
      });
    });
    
    return results;
  } catch (error) {
    console.error('Error fetching all Monthly Campaign Results data:', error);
    throw error;
  }
}

/**
 * Get Monthly Campaign Results data for specific campaign types
 * @param companyName - The company name
 * @param asin - The ASIN to get data for
 * @param campaignTypes - Array of campaign types to filter by
 * @returns Promise<MonthlyCampaignResult[]>
 */
export async function getMonthlyCampaignResultsByCampaignType(
  companyName: string,
  asin: string,
  campaignTypes: string[]
): Promise<MonthlyCampaignResult[]> {
  try {
    const campaignData = await getMonthlyCampaignResultsData(companyName, asin);
    
    if (!campaignData) {
      return [];
    }
    
    return campaignData.campaigns.filter(campaign => 
      campaignTypes.includes(campaign.campaignType)
    );
  } catch (error) {
    console.error('Error fetching Monthly Campaign Results by campaign type:', error);
    throw error;
  }
}

/**
 * Get aggregated metrics for all campaigns of an ASIN
 * @param companyName - The company name
 * @param asin - The ASIN to get data for
 * @returns Promise<MonthlyCampaignResult | null> - Aggregated totals
 */
export async function getAggregatedMonthlyCampaignResults(
  companyName: string,
  asin: string
): Promise<MonthlyCampaignResult | null> {
  try {
    const campaignData = await getMonthlyCampaignResultsData(companyName, asin);
    
    if (!campaignData || campaignData.campaigns.length === 0) {
      return null;
    }
    
    const aggregated = campaignData.campaigns.reduce(
      (acc, campaign) => ({
        uniqueCode: 'AGGREGATED',
        campaignType: 'All Campaigns',
        impressions: acc.impressions + campaign.impressions,
        clicks: acc.clicks + campaign.clicks,
        spend: acc.spend + campaign.spend,
        sales: acc.sales + campaign.sales,
        orders: acc.orders + campaign.orders,
        units: acc.units + campaign.units
      }),
      {
        uniqueCode: 'AGGREGATED',
        campaignType: 'All Campaigns',
        impressions: 0,
        clicks: 0,
        spend: 0,
        sales: 0,
        orders: 0,
        units: 0
      }
    );
    
    return aggregated;
  } catch (error) {
    console.error('Error calculating aggregated Monthly Campaign Results:', error);
    throw error;
  }
}